import RiskHitFeatureBlock from './RiskHitFeatureBlock';

const RADIO_LABELS = {
  wifi: 'Wi‑Fi',
  nbiot: 'NB‑IoT',
};

/** Typical NB-IoT / Wi‑Fi usable range for the meter scale. */
const DBM_MIN = -120;
const DBM_MAX = -50;

/** Full-track quality gradient: weak → medium → good (left → right). */
const TRACK_GRADIENT =
  'linear-gradient(90deg, #f87171 0%, #fb923c 28%, #fbbf24 52%, #a3e635 78%, #34d399 100%)';

function clampPct(dbm) {
  if (dbm == null || !Number.isFinite(Number(dbm))) return null;
  const v = Number(dbm);
  return Math.max(0, Math.min(100, ((v - DBM_MIN) / (DBM_MAX - DBM_MIN)) * 100));
}

function qualityMeta(dbm) {
  if (dbm == null || !Number.isFinite(Number(dbm))) {
    return {
      label: 'нет данных',
      textClass: 'text-slate-500',
      tip: '#94a3b8',
      glow: 'rgba(148,163,184,0.35)',
    };
  }
  const v = Number(dbm);
  if (v >= -80) {
    return {
      label: 'Хороший',
      textClass: 'text-emerald-700 dark:text-emerald-300',
      tip: '#34d399',
      glow: 'rgba(52,211,153,0.45)',
    };
  }
  if (v >= -100) {
    return {
      label: 'Средний',
      textClass: 'text-amber-700 dark:text-amber-300',
      tip: '#fbbf24',
      glow: 'rgba(251,191,36,0.45)',
    };
  }
  return {
    label: 'Слабый',
    textClass: 'text-red-700 dark:text-red-300',
    tip: '#f87171',
    glow: 'rgba(248,113,113,0.45)',
  };
}

/**
 * Same-size feature strip as the transmission timeline — last signal level.
 * One wide color scale (no cellular bar tree); tip color = last dBm band.
 */
export default function Risk3SignalFeature({ dbm, radio, lastAtLabel }) {
  const pct = clampPct(dbm);
  const meta = qualityMeta(dbm);
  const radioLabel = RADIO_LABELS[radio] || radio || 'радио';
  const hasValue = pct != null;

  return (
    <RiskHitFeatureBlock title={`Последний сигнал · ${radioLabel}`}>
      <div className="flex h-full flex-col justify-center gap-2.5">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p className={`text-2xl font-semibold tabular-nums tracking-tight ${meta.textClass}`}>
              {dbm == null || !Number.isFinite(Number(dbm)) ? '—' : `${Number(dbm)}`}
              <span className="ml-1 text-sm font-medium text-slate-500">дБм</span>
            </p>
            <p className={`mt-0.5 text-xs font-medium ${meta.textClass}`}>{meta.label}</p>
          </div>
          {lastAtLabel ? (
            <p className="shrink-0 pb-0.5 text-[10px] tabular-nums text-slate-400">{lastAtLabel}</p>
          ) : null}
        </div>

        <div className="w-full">
          <div
            className="relative h-4 w-full overflow-visible rounded-full bg-slate-200/90 shadow-inner dark:bg-slate-800"
            role="meter"
            aria-label="Уровень сигнала"
            aria-valuemin={DBM_MIN}
            aria-valuemax={DBM_MAX}
            aria-valuenow={hasValue ? Number(dbm) : undefined}
            aria-valuetext={hasValue ? `${Number(dbm)} дБм, ${meta.label}` : meta.label}
          >
            {/* Soft full spectrum underlay */}
            <div
              className="absolute inset-0 rounded-full opacity-25 dark:opacity-20"
              style={{ background: TRACK_GRADIENT }}
            />
            {/* Active fill: same gradient, clipped so tip color matches last signal */}
            {hasValue ? (
              <div
                className="absolute inset-y-0 left-0 overflow-hidden rounded-full"
                style={{ width: `${pct}%` }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${10000 / Math.max(pct, 0.01)}%`,
                    maxWidth: 'none',
                    background: TRACK_GRADIENT,
                    boxShadow: `0 0 12px ${meta.glow}`,
                  }}
                />
              </div>
            ) : null}
            {hasValue ? (
              <span
                className="absolute top-1/2 z-10 block h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white shadow-md dark:border-slate-900"
                style={{
                  left: `${pct}%`,
                  backgroundColor: meta.tip,
                  boxShadow: `0 0 0 3px ${meta.glow}, 0 2px 6px rgba(15,23,42,0.25)`,
                }}
              />
            ) : null}
          </div>
          <div className="mt-1.5 flex justify-between text-[10px] tabular-nums text-slate-400">
            <span>{DBM_MIN}</span>
            <span className="text-slate-500">слабый → хороший</span>
            <span>{DBM_MAX} дБм</span>
          </div>
        </div>
      </div>
    </RiskHitFeatureBlock>
  );
}
